import { AuthRoute } from '@/src/routes/routes.components'
import { LayoutMain } from '@layouts/LayoutMain/LayoutMain'
import { MEASURES_BASE_ROUTE, TABS } from '@utils/constants'
import React, { lazy, Suspense } from 'react'
import { Navigate, type RouteObject } from 'react-router-dom'
import { EnPIProvider } from '../iso-suite/enpi/EnPIContext'
import { EnpiLegacyRedirect } from '../iso-suite/enpi/enpi-legacy-redirect'
import { ENPI_MESSSTELLEN_BASE_PATH } from '../iso-suite/enpi/enpi-paths'

const MeasureHubPageLazy = lazy(() => import('./MeasureHubSceen.page'))
const ISOMeasureV2PageLazy = lazy(() => import('@features/iso-suite/measures/IsoMeasuresV2.page'))
const ISOMeasureDetailPageLazy = lazy(() => import('@features/iso-suite/measures/mesaure-detail/IsoMeasuresDetail.page'))
const ISOSuiteValeriPageLazy = lazy(() => import('@features/iso-suite/valeri/iso-valeri.screen'))
const IsoValeriDetaiPageLazy = lazy(() => import('@features/iso-suite/valeri/IsoValeriDetail.page'))
const ISOValeriEditPageLazy = lazy(() => import('@features/iso-suite/valeri/IsoValeriEdit.page'))

const AnalyticsBaselineDetailsLazy = React.lazy(() => import('@features/analytics/baseline/AnalyticsBaselineDetails.page'))
const AnalyticsBaselineOverviewLazy = React.lazy(() => import('@features/analytics/baseline/AnalyticsBaselineOverview.page'))
const SimulationOverviewPageLazy = React.lazy(() => import('./simulation/SimulationOverview.page'))
const SimulationDetailPageLazy = React.lazy(() => import('./simulation/SimulationDetail.page'))

const ENPILazy = lazy(() => import('@features/iso-suite/enpi/ENPI'))
const EnPIDetailsPageLazy = lazy(() => import('@features/iso-suite/enpi/EnPIDetails.page'))
const ISOSuiteCreateEditEnPIPageLazy = lazy(() => import('@features/iso-suite/enpi/CreateEditEnPI.page'))

export const getMeasureHubRoutes = (isEasyOfferEnpiEnabled: boolean): RouteObject[] => {
  const defaultRedirection = TABS.measureHub.measures

  const MeasureHubPage = (
    <Suspense fallback={<div></div>}>
      <EnPIProvider>
        <MeasureHubPageLazy />
      </EnPIProvider>
    </Suspense>
  )

  const ISOMeasuresPage = () => {
    return (
      <Suspense fallback={<div></div>}>
        <ISOMeasureV2PageLazy />
      </Suspense>
    )
  }

  const ISOMeasuresDetailPage = (
    <Suspense fallback={<div></div>}>
      <ISOMeasureDetailPageLazy />
    </Suspense>
  )

  const ISOSuiteValeriPage = (
    <Suspense fallback={<div></div>}>
      <ISOSuiteValeriPageLazy />
    </Suspense>
  )

  const ValeriDetailPage = (
    <Suspense fallback={<div></div>}>
      <IsoValeriDetaiPageLazy />
    </Suspense>
  )

  const ISOValeriEditPage = (
    <Suspense fallback={<div></div>}>
      <ISOValeriEditPageLazy />
    </Suspense>
  )

  const BaselineAnalyticsOverviewPage = (
    <Suspense fallback={<div></div>}>
      <AnalyticsBaselineOverviewLazy />
    </Suspense>
  )

  const BaseLineAnalyticsDetailsPage = (
    <Suspense fallback={<div></div>}>
      <AnalyticsBaselineDetailsLazy />
    </Suspense>
  )

  const SimulationOverviewPage = (
    <Suspense fallback={<div></div>}>
      <SimulationOverviewPageLazy />
    </Suspense>
  )

  const SimulationDetailPage = (
    <Suspense fallback={<div></div>}>
      <SimulationDetailPageLazy />
    </Suspense>
  )
  const ENPI = (
    <Suspense fallback={<div></div>}>
      <ENPILazy />
    </Suspense>
  )

  const EnPIDetailsPage = (
    <Suspense fallback={<div></div>}>
      <EnPIDetailsPageLazy />
    </Suspense>
  )

  const ISOSuiteCreateEditEnPIPage = (
    <Suspense fallback={<div></div>}>
      <ISOSuiteCreateEditEnPIPageLazy />
    </Suspense>
  )
  return [
    {
      path: MEASURES_BASE_ROUTE,
      element: (
        <AuthRoute>
          <LayoutMain />
        </AuthRoute>
      ),
      children: [
        { path: '', element: <Navigate replace to={defaultRedirection} /> },
        {
          path: TABS.measureHub.simulation,
          element: MeasureHubPage,
          children: [{ path: '', element: SimulationOverviewPage }],
        },
        {
          path: 'simulation/:simulationId',
          element: SimulationDetailPage,
        },
        {
          path: TABS.measureHub.measures,
          element: MeasureHubPage,
          children: [
            { path: '', element: <ISOMeasuresPage /> },
            { path: ':measureId', element: ISOMeasuresDetailPage },
          ],
        },
        {
          path: TABS.measureHub.valeri,
          element: MeasureHubPage,
          children: [
            { path: '', element: ISOSuiteValeriPage },
            { path: ':valeriId', element: ValeriDetailPage },
            { path: ':valeriId/edit', element: ISOValeriEditPage },
          ],
        },
        {
          path: TABS.measureHub.analyticsBaseline,
          element: MeasureHubPage,
          children: [
            { path: '', element: BaselineAnalyticsOverviewPage },
            { path: ':baselineId', element: BaseLineAnalyticsDetailsPage },
          ],
        },
        ...(isEasyOfferEnpiEnabled
          ? // EC-5827: EnPI now lives at ENPI_MESSSTELLEN_BASE_PATH - keep old bookmarks/links working via redirects
            [
              { path: 'enpi', element: <Navigate replace to={ENPI_MESSSTELLEN_BASE_PATH} /> },
              { path: 'enpi/new', element: <Navigate replace to={`${ENPI_MESSSTELLEN_BASE_PATH}/new`} /> },
              {
                path: 'enpi/:enpiId/edit',
                element: <EnpiLegacyRedirect suffix={({ enpiId }) => `/${enpiId}/edit`} />,
              },
              { path: 'enpi/:enpiId', element: <EnpiLegacyRedirect suffix={({ enpiId }) => `/${enpiId}`} /> },
            ]
          : [
              {
                path: 'enpi',
                element: MeasureHubPage,
                children: [
                  { path: '', element: ENPI },
                  { path: 'new', element: ISOSuiteCreateEditEnPIPage },
                  { path: ':enpiId/edit', element: ISOSuiteCreateEditEnPIPage },
                  { path: ':enpiId', element: EnPIDetailsPage },
                ],
              },
            ]),
      ],
    },
  ]
}
